// A regra do CST e ClassTrib (000223) — pura, com prova própria
// (`app/tests/tabela-cst.mjs`).
//
// ---------------------------------------------------------------------------
// O que este módulo resolve
// ---------------------------------------------------------------------------
// A Receita publica a mesma informação em DUAS listas, e nenhuma delas basta:
//
//   * `situacoes-tributarias/*` diz **a qual CST cada classificação pertence**,
//     com dois campos por classificação (código e descrição);
//   * `classificacoes-tributarias/*` traz a **ficha completa** de cada uma
//     (tipo de alíquota, nomenclatura, os quatro indicadores de crédito, os três
//     percentuais de redução, os tipos de DFe) e **não diz o CST**.
//
// A tela precisa das duas, e juntar é o trabalho daqui.
//
// ---------------------------------------------------------------------------
// As duas listas DISCORDAM, e isso não é defeito nosso
// ---------------------------------------------------------------------------
// Medido em 14/09, na mesma data: a lista por CST traz **164** pares e o
// catálogo traz **161**. As três de diferença são as de "Alíquota fixa"
// (220001, 220002, 220003), que saíram do catálogo e permaneceram na lista por
// CST — em 2026-01-01 as duas listas tinham 164 e concordavam.
//
// A classificação sem ficha entra na tela com `semFicha: true` e um aviso, em
// vez de sumir ou aparecer com campos em branco. Sumir esconderia da tela um
// código que a Receita ainda associa ao CST; mostrar em branco faria o aluno
// ler "sem redução" onde está escrito "não sei".

export type FichaDaReceita = {
  codigo: string;
  descricao: string;
  tipoAliquota?: string | null;
  nomenclatura?: string | null;
  descricaoTratamentoTributario?: string | null;
  incompativelComSuspensao?: boolean;
  exigeGrupoDesoneracao?: boolean;
  possuiPercentualReducao?: boolean;
  indicaApropriacaoCreditoAdquirenteCbs?: boolean;
  indicaApropriacaoCreditoAdquirenteIbs?: boolean;
  indicaCreditoPresumidoFornecedor?: boolean;
  indicaCreditoPresumidoAdquirente?: boolean;
  creditoOperacaoAntecedente?: string | null;
  percentualReducaoCbs?: number | null;
  percentualReducaoIbsUf?: number | null;
  percentualReducaoIbsMun?: number | null;
  tiposDfeClassificacao?: { tipo: number; sigla: string; descricao: string }[];
  dataAtualizacao?: string | null;
};

export type CstDaReceita = {
  codigo: string;
  descricao: string;
  classificacoesTributarias: { codigo: string; descricao: string }[];
};

export type Classificacao = FichaDaReceita & {
  cst: string;
  /** A ficha não veio do catálogo — ver o comentário do topo. */
  semFicha: boolean;
};

export type Cst = {
  codigo: string;
  descricao: string;
  classificacoes: Classificacao[];
};

export type Tabela = {
  csts: Cst[];
  /** Quantas classificações ao todo — o número que a tela mostra. */
  totalClassificacoes: number;
  /** As que a lista por CST tem e o catálogo não. A tela DIZ quantas são. */
  semFicha: string[];
};

/**
 * Junta as duas listas do gov numa árvore CST → classificações.
 *
 * A lista por CST é o ESQUELETO, e não o catálogo: é ela que tem a relação de
 * pertencimento, que é o eixo da ferramenta. O catálogo entra como detalhe,
 * casado por código.
 */
export function montarTabela(csts: CstDaReceita[], catalogo: FichaDaReceita[]): Tabela {
  const porCodigo = new Map(catalogo.map((c) => [c.codigo, c]));
  const semFicha: string[] = [];
  let total = 0;

  const arvore: Cst[] = (csts ?? []).map((c) => ({
    codigo: c.codigo,
    descricao: c.descricao,
    classificacoes: (c.classificacoesTributarias ?? []).map((cl) => {
      total += 1;
      const ficha = porCodigo.get(cl.codigo);
      if (!ficha) semFicha.push(cl.codigo);
      return {
        // A descrição do CATÁLOGO tem precedência sobre a do esqueleto.
        //
        // Medido em 14/09: hoje as duas fontes do gov trazem o MESMO texto em
        // todas as 161 que casam — **zero divergências**. (As 19 que o runbook
        // cita são entre o app de ORIGEM e o gov, outra comparação.) A
        // precedência existe porque o catálogo é a fonte rica, que carrega a
        // ficha inteira; se um dia os dois discordarem, é dele que a tela
        // mostra o resto, e misturar a descrição de um com os campos do outro
        // daria uma linha que não existe em fonte nenhuma.
        //
        // Sem divergência no dado, isto NÃO é observável por execução — a
        // sabotagem que inverte a precedência passa verde. Quem vigia é este
        // comentário com a medição ao lado, e a prova pura, que monta o caso
        // artificialmente (bloco 2 de `tests/tabela-cst.mjs`).
        ...(ficha ?? { codigo: cl.codigo, descricao: cl.descricao }),
        descricao: ficha?.descricao ?? cl.descricao,
        cst: c.codigo,
        semFicha: !ficha,
      };
    }),
  }));

  return { csts: arvore, totalClassificacoes: total, semFicha };
}

// ---------------------------------------------------------------------------
// A busca
// ---------------------------------------------------------------------------
/**
 * Sem acento e em minúsculas, para "reducao" achar "redução".
 *
 * A mesma normalização do SindiFácil e da busca de ocupações do MEI. Ela é NFD
 * + remoção de diacríticos, e não uma tabela de trocas escrita à mão: lista de
 * pares se levanta do dado e envelhece, e aqui o dado é a norma inteira.
 */
export function semAcento(texto: string): string {
  return (texto ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

/** Onde o termo casou — a tela mostra isso, para o aluno saber por que a linha veio. */
export type Casamento = "codigo-cst" | "codigo-classificacao" | "texto" | null;

export function casaClassificacao(c: Classificacao, termo: string): Casamento {
  const t = semAcento(termo);
  if (t === "") return null;
  if (c.codigo.includes(t)) return "codigo-classificacao";
  if (c.cst.includes(t)) return "codigo-cst";
  // O tratamento tributário é o texto do CST repetido na ficha, e entra na
  // busca de propósito: quem procura "monofásica" está procurando o grupo, e
  // não uma palavra que apareça na descrição de uma classificação só.
  const campos = [c.descricao, c.descricaoTratamentoTributario, c.tipoAliquota, c.nomenclatura];
  return campos.some((x) => semAcento(String(x ?? "")).includes(t)) ? "texto" : null;
}

export type Filtros = {
  termo: string;
  /** Só as que têm percentual de redução. */
  comReducao: boolean;
  /** Só as que dão crédito ao adquirente (CBS ou IBS). */
  comCredito: boolean;
  /** Só as que geram crédito presumido (fornecedor ou adquirente). */
  comCreditoPresumido: boolean;
  /** A sigla do DFe (NF-e, NFS-e…), ou vazio para todos. */
  dfe: string;
  /** Só os códigos favoritados. */
  soFavoritos: boolean;
};

export const FILTROS_VAZIOS: Filtros = {
  termo: "",
  comReducao: false,
  comCredito: false,
  comCreditoPresumido: false,
  dfe: "",
  soFavoritos: false,
};

export function temFiltroLigado(f: Filtros): boolean {
  return (
    f.termo.trim() !== "" ||
    f.comReducao ||
    f.comCredito ||
    f.comCreditoPresumido ||
    f.dfe !== "" ||
    f.soFavoritos
  );
}

/**
 * A chave de um favorito. É ela que a tela compara, e o formato importa: o
 * código `000001` existe nas DUAS tabelas da Receita com significados
 * diferentes, então o tributo entra na chave.
 */
export function chaveDoFavorito(
  tributo: string,
  cst: string,
  cclasstrib: string | null,
): string {
  return `${tributo}|${cst}|${cclasstrib ?? ""}`;
}

export type Resultado = {
  csts: Cst[];
  /** Quantas classificações sobraram depois dos filtros. */
  quantas: number;
  /** Quantos CSTs têm pelo menos uma classificação visível. */
  quantosCsts: number;
};

/**
 * Aplica os filtros e devolve a árvore PODADA.
 *
 * Um CST só aparece quando tem classificação visível — e a exceção é a busca
 * por código de CST, que traz o grupo inteiro: quem digita "410" quer as 22
 * classificações de imunidade, não uma.
 */
export function filtrar(
  tabela: Tabela,
  f: Filtros,
  favoritos: Set<string>,
  tributo: string,
): Resultado {
  const termo = f.termo.trim();
  let quantas = 0;

  const csts: Cst[] = [];
  for (const cst of tabela.csts) {
    // Quem traz o GRUPO inteiro quando se digita "410" é `casaClassificacao`,
    // pelo ramo `c.cst.includes(t)` — cada classificação sabe o CST dela.
    //
    // Havia aqui um segundo caminho (`cst.codigo === termo` liberando o grupo
    // todo), e a SABOTAGEM mostrou que ele era redundante: `includes` engloba a
    // igualdade, então desligá-lo não mudava resultado nenhum, nem no dado real
    // nem num caso montado à mão. Código que não muda nada é a cicatriz da
    // `000106` — as cinco funções de calendário no banco com zero chamadas —, e
    // a saída é a mesma: remover, não documentar.
    const visiveis = cst.classificacoes.filter((c) => {
      if (f.comReducao && !temReducao(c)) return false;
      if (f.comCredito && !daCredito(c)) return false;
      if (f.comCreditoPresumido && !temCreditoPresumido(c)) return false;
      if (f.dfe !== "" && !valeNoDfe(c, f.dfe)) return false;
      if (f.soFavoritos && !ehFavorito(c, favoritos, tributo)) return false;
      if (termo !== "" && casaClassificacao(c, termo) === null) return false;
      return true;
    });

    if (visiveis.length === 0) continue;
    quantas += visiveis.length;
    csts.push({ ...cst, classificacoes: visiveis });
  }

  return { csts, quantas, quantosCsts: csts.length };
}

function ehFavorito(c: Classificacao, favoritos: Set<string>, tributo: string): boolean {
  return (
    favoritos.has(chaveDoFavorito(tributo, c.cst, c.codigo)) ||
    // O favorito de CST INTEIRO faz todas as filhas dele aparecerem no filtro.
    // Sem isto, marcar o grupo e filtrar por favoritos devolveria lista vazia —
    // o aluno guardaria o 410 e não veria nenhuma das 22.
    favoritos.has(chaveDoFavorito(tributo, c.cst, null))
  );
}

/**
 * Tem redução de alíquota?
 *
 * A pergunta é sobre os PERCENTUAIS, e não sobre a bandeira
 * `possuiPercentualReducao` sozinha: medido em 14/09, 60 das 161 classificações
 * trazem a bandeira ligada, e os percentuais são o que o contador usa. Ler as
 * duas coisas mantém a resposta certa se uma delas vier vazia num dia.
 */
export function temReducao(c: Classificacao): boolean {
  if (c.possuiPercentualReducao === true) return true;
  return [c.percentualReducaoCbs, c.percentualReducaoIbsUf, c.percentualReducaoIbsMun].some(
    (p) => typeof p === "number" && p > 0,
  );
}

export function daCredito(c: Classificacao): boolean {
  return c.indicaApropriacaoCreditoAdquirenteCbs === true ||
    c.indicaApropriacaoCreditoAdquirenteIbs === true;
}

export function temCreditoPresumido(c: Classificacao): boolean {
  return c.indicaCreditoPresumidoFornecedor === true ||
    c.indicaCreditoPresumidoAdquirente === true;
}

export function valeNoDfe(c: Classificacao, sigla: string): boolean {
  return (c.tiposDfeClassificacao ?? []).some((d) => d.sigla === sigla);
}

/** As siglas de DFe que aparecem no catálogo, para o combo não oferecer vazio. */
export function siglasDeDfe(tabela: Tabela): string[] {
  const siglas = new Set<string>();
  for (const cst of tabela.csts) {
    for (const c of cst.classificacoes) {
      for (const d of c.tiposDfeClassificacao ?? []) siglas.add(d.sigla);
    }
  }
  return [...siglas].sort((a, b) => a.localeCompare(b, "pt-BR"));
}

/**
 * O percentual como texto.
 *
 * Zero é um VALOR ("não reduz") e nulo é "o catálogo não diz" — as duas coisas
 * são diferentes, e escrever "0%" onde não há dado afirma uma redução que
 * ninguém apurou. É a cicatriz do teto do MEI, onde `?? 0` escrevia "de R$ 0,00"
 * num livro vazio.
 */
export function percentualEmTexto(p: number | null | undefined): string {
  if (typeof p !== "number" || !Number.isFinite(p)) return "—";
  return p.toLocaleString("pt-BR", { maximumFractionDigits: 2 }) + "%";
}

/** A concordância, que é código e não redação. */
export function classificacoesEmTexto(n: number): string {
  return n === 1 ? "1 classificação" : `${n} classificações`;
}

export function cstsEmTexto(n: number): string {
  return n === 1 ? "1 CST" : `${n} CSTs`;
}

/**
 * A data padrão da consulta: HOJE.
 *
 * O catálogo é parametrizado por data, e a tela nasce na data de hoje porque é
 * a pergunta que o contador faz ("o que vale agora?"). O seletor existe porque a
 * resposta MUDA: medido em 14/09, 164 classificações em 2026-01-01 e 161 em
 * 2026-06-01. A ferramenta de origem, com um arquivo estático, não tinha como
 * mostrar isso.
 */
export function hojeEmIso(agora = new Date()): string {
  // Fuso de Brasília, e não UTC: às 21h de São Paulo o UTC já é o dia seguinte,
  // e a tela abriria no amanhã. O catálogo muda por data de vigência, então um
  // dia a mais pode trocar a resposta.
  const brasilia = new Date(agora.getTime() - 3 * 3600_000);
  return brasilia.toISOString().slice(0, 10);
}
